import { isArray } from 'lodash';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { components } from 'react-select';
import { VariableSizeList as VariableWindowList } from 'react-window';
import styled from 'styled-components';
import { CollapseIcon } from 'components/atoms/collapse/collapse-icon';
import { optionHeight } from 'components/atoms/select/constants';

const VirtualizationContext = createContext({} as any);

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  padding-left: var(--spacing-05);
  top: 50%;
  position: relative;
  transform: translateY(-50%);
`;

const StickyHeaderWrapper = styled.div`
  left: 0;
  right: 0;

  .sticky {
    position: sticky;
    top: 0;
    z-index: 999;
    background: #fff;
    height: 36px;
    color: var(--gray-50) !important;
    &:hover {
      background: var(--coolest-gray-10) !important;
    }
  }
`;

const GroupHeading = (props) => {
  const { label } = props;

  return <components.GroupHeading {...props}>{label}</components.GroupHeading>;
};

const StickyRow = ({ style, children }) => (
  <StickyHeaderWrapper style={style}>
    <div className="sticky">{children}</div>
  </StickyHeaderWrapper>
);

// List Virtualization
const computeMenuItemHeight = (option) =>
  option.type === 'groupHeading'
    ? optionHeight.groupHeading
    : optionHeight.basic;

const flattenGroupedChildrens = (
  children: React.ReactElement[],
  activeIndices: number[],
) => {
  const options = children.map((child, index) => {
    const tempOptions = child.props.data.__isNew__
      ? []
      : [
          {
            props: {
              data: {
                type: 'groupHeading',
                label: child.props?.data?.label,
              },
            },
          },
        ];

    if (child.props.type !== 'option') {
      tempOptions.push(
        ...(activeIndices.includes(index) ? child.props?.children : []),
      );
    } else {
      tempOptions.push(child.props?.children);
    }

    return tempOptions;
  });

  const allValues = options.reduce(
    (accumulator, currentValue) => accumulator.concat(currentValue),
    [],
  );

  return allValues;
};

const RowRenderer = ({ index, style, data }) => {
  return (
    <div style={style}>
      {data[index]?.props?.data?.type === 'groupHeading' ? null : data[index]}
    </div>
  );
};

const getWrapperHeightForStickyHeader = (options): number =>
  options.reduce(
    (prev, _option) => prev + computeMenuItemHeight(_option.data),
    optionHeight.groupHeading,
  );

const innerElementType = forwardRef(({ children, ...rest }) => {
  const getTop = useCallback(
    (index: number, stickyWrapperHeights: number[]): number => {
      return (
        stickyWrapperHeights
          .slice(0, index)
          .reduce((prev, current) => prev + current, 0) || 0
      );
    },
    [],
  );

  return (
    <VirtualizationContext.Consumer>
      {({
        groups,
        activeIndices,
        onHeadingClick,
        groupHeadingExtraProps,
        grouped,
      }) => {
        const stickyWrapperHeights: number[] = [];

        return (
          <div {...rest}>
            {grouped &&
              groups.map((_group, index) => {
                const {
                  children: _children,
                  data,
                  ...headingPropsRest
                } = _group.props;

                let height = 300;
                let isActive = false;

                if (isArray(_children)) {
                  const _options = _children.map((_child) => _child.props);

                  isActive = activeIndices.includes(index);

                  height = isActive
                    ? getWrapperHeightForStickyHeader(_options)
                    : optionHeight.groupHeading;

                  stickyWrapperHeights.push(height);
                }

                if (data.__isNew__) {
                  return <></>;
                }

                return (
                  <StickyRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    style={{
                      top: `${getTop(index, stickyWrapperHeights)}px`,
                      height,
                    }}
                  >
                    <AccordionHeader onClick={() => onHeadingClick(index)}>
                      <CollapseIcon className={isActive ? 'active' : ''} />
                      <GroupHeading
                        {...headingPropsRest}
                        data={data}
                        {...groupHeadingExtraProps}
                      />
                    </AccordionHeader>
                  </StickyRow>
                );
              })}
            {children}
          </div>
        );
      }}
    </VirtualizationContext.Consumer>
  );
});

const VariableListRowRenderer = (props) => {
  const {
    children,
    selectProps: { maxMenuHeight, grouped },
    groupHeadingExtraProps,
  } = props;

  const filteredChildren = children.filter(
    (child) => !child.props.data?.__isNew__ === true,
  );
  const createActivity = children.find(
    (child) => child.props.data.__isNew__ === true,
  );

  const [activeIndices, setActiveIndices] = useState([
    ...Array(filteredChildren.length).keys(),
  ]);

  const onHeadingClick = useCallback(
    (index) => {
      setActiveIndices(
        activeIndices.includes(index)
          ? activeIndices.filter((i) => index !== i)
          : [...activeIndices, index],
      );
    },
    [activeIndices],
  );

  const transformedChild = useMemo(
    () =>
      grouped
        ? flattenGroupedChildrens(filteredChildren, activeIndices)
        : filteredChildren,
    [activeIndices, filteredChildren, grouped],
  );

  const listRef = React.useRef<VariableWindowList>(null);

  useEffect(() => {
    const resetWindowedListCachePosition = () => {
      transformedChild.forEach((_, index) => {
        if (listRef.current) {
          listRef.current.resetAfterIndex(index);
        }
      });
    };

    resetWindowedListCachePosition();
  }, [transformedChild]);

  const _transformedChild = [...transformedChild, createActivity];

  return (
    <VirtualizationContext.Provider
      value={{
        groups: children,
        activeIndices,
        onHeadingClick,
        groupHeadingExtraProps,
        grouped,
      }}
    >
      <VariableWindowList
        height={maxMenuHeight}
        itemCount={_transformedChild.length}
        innerElementType={innerElementType}
        itemData={_transformedChild}
        ref={listRef}
        itemSize={(index: number) =>
          computeMenuItemHeight(_transformedChild[index]?.props?.data || [])
        }
        width="100%"
      >
        {RowRenderer}
      </VariableWindowList>
    </VirtualizationContext.Provider>
  );
};

export const VirtualizedMenuList = (props): JSX.Element => {
  const { children } = props;

  // For handling no options
  if (children && !Array.isArray(children)) {
    return children;
  }

  return <VariableListRowRenderer {...props} />;
};
